/**
 * The HTTP contract, as zod schemas.
 *
 * Fastify validates requests against these; the web client infers its types
 * from the same objects. One definition, both ends.
 */
import { z } from 'zod'
import {
  AFFECTS,
  BASES,
  DIMENSION_CODES,
  FACT_CODES,
  HEDGES,
  MAX_SCORE,
  MIN_SCORE,
  MOVE_TYPES,
  PLATFORM_ROLES,
  ROLES,
  SIZE_BANDS,
  TENANT_ROLES,
} from './vocab.js'

export const scoreSchema = z.number().int().min(MIN_SCORE).max(MAX_SCORE)

/* -------------------------------------------------------------------------- */
/* auth                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Password floor is length, not composition. Length is what actually resists
 * guessing, and composition rules push people toward `Passw0rd!`.
 */
export const passwordSchema = z
  .string()
  .min(12, 'گذرواژه باید دست‌کم ۱۲ نویسه باشد')
  .max(200)

export const loginSchema = z.object({
  email: z.string().email().max(320),
  password: z.string().min(1).max(200),
})
export type LoginRequest = z.infer<typeof loginSchema>

/** Every account starts with a password an admin chose; this is how it stops being theirs. */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
})
export type ChangePasswordRequest = z.infer<typeof changePasswordSchema>

/**
 * `org` is the organization the session is *acting as*, which for an
 * impersonating super admin is the charity rather than their own. Everything in
 * the app therefore keeps reading one field and needs no notion of
 * impersonation — except the banner, which reads `impersonating`.
 */
export const sessionUserSchema = z.object({
  id: z.number().int(),
  email: z.string(),
  displayName: z.string().nullable(),
  role: z.enum(ROLES),
  org: z.object({
    id: z.number().int(),
    name: z.string(),
    legacyCharityId: z.number().int().nullable(),
  }),
  mustChangePassword: z.boolean().default(false),
  /** A staff account in its own sandbox: nothing it does here reaches a charity. */
  personalSpace: z.boolean().default(false),
  impersonating: z
    .object({
      orgId: z.number().int(),
      orgName: z.string(),
      startedOn: z.string(),
      reason: z.string().nullable(),
    })
    .nullable()
    .default(null),
})
export type SessionUser = z.infer<typeof sessionUserSchema>

/* -------------------------------------------------------------------------- */
/* journey                                                                     */
/* -------------------------------------------------------------------------- */

/** One pickable answer, already mapped to the 0–3 score it means. */
export const moveOptionSchema = z.object({
  score: scoreSchema,
  labelFa: z.string(),
})
export type MoveOption = z.infer<typeof moveOptionSchema>

export const moveStatementSchema = z.object({
  code: z.string(),
  levelNo: z.number().int(),
  dimensionCode: z.enum(DIMENSION_CODES),
  dimensionTitleFa: z.string(),
  textFa: z.string(),
})

export const progressSchema = z.object({
  known: z.number().int(),
  total: z.number().int(),
  turnsThisSession: z.number().int(),
})

/**
 * Extra payload a move may carry beyond a question.
 *
 * A move is not always "here is a question, pick a number". Asking for a
 * document, showing a contradiction, proposing an action and collecting a
 * completion claim all need their own shape, and flattening them into free text
 * would lose the structure the UI needs to render them honestly.
 */
export const movePayloadSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('document_request'),
    /** The exact document being asked for, and what it would settle. */
    evidenceTypeFa: z.string(),
    provesFa: z.string(),
  }),
  z.object({
    kind: z.literal('challenge'),
    quoteFa: z.string(),
    documentFilename: z.string(),
    pageNo: z.number().int().nullable(),
    /** What they told us, which the document disagrees with. */
    selfReportedScore: scoreSchema.nullable(),
  }),
  z.object({
    kind: z.literal('recommendation'),
    contentId: z.number().int(),
    titleFa: z.string(),
    url: z.string().nullable(),
    contentKind: z.string(),
  }),
  z.object({
    kind: z.literal('commitment'),
    actionFa: z.string(),
    doneLooksLikeFa: z.string(),
  }),
  z.object({
    kind: z.literal('claim'),
    commitmentId: z.number().int(),
    actionFa: z.string(),
    doneLooksLikeFa: z.string(),
  }),
])
export type MovePayload = z.infer<typeof movePayloadSchema>

/**
 * A single move, ready to render.
 *
 * `generated: false` means the wording came from stored fallback text because
 * the model was unreachable — the UI shows a different provenance badge for it.
 * A fallback plan must never be mistaken for a generated one.
 */
export const moveSchema = z.object({
  turnId: z.number().int(),
  type: z.enum(MOVE_TYPES),
  /** The agent's words. Persian, RTL. */
  textFa: z.string(),
  /** Shown above the question when the concept likely needs explaining first. */
  teachFa: z.string().nullable(),
  statement: moveStatementSchema.nullable(),
  options: z.array(moveOptionSchema),
  allowFreeText: z.boolean(),
  allowNotApplicable: z.boolean(),
  allowSkip: z.boolean(),
  progress: progressSchema,
  generated: z.boolean(),
  model: z.string().nullable(),
  /** Set when the session resumes after a gap, so the UI can say so. */
  resumedAfterDays: z.number().int().nullable(),
  payload: movePayloadSchema.nullable(),
})
export type Move = z.infer<typeof moveSchema>

export const answerSchema = z
  .object({
    turnId: z.number().int(),
    score: scoreSchema.nullable().optional(),
    textFa: z.string().max(4000).optional(),
    notApplicable: z.boolean().optional(),
    /** For a proposed action: taken on, or declined. */
    accept: z.boolean().optional(),
  })
  .refine(
    (a) =>
      a.score !== undefined ||
      (a.textFa && a.textFa.trim().length > 0) ||
      a.notApplicable ||
      a.accept !== undefined,
    { message: 'پاسخ خالی است' },
  )
export type AnswerRequest = z.infer<typeof answerSchema>

export const skipSchema = z.object({
  turnId: z.number().int(),
  reasonFa: z.string().max(500).optional(),
})
export type SkipRequest = z.infer<typeof skipSchema>

/** What the interpreter concluded, surfaced so the charity can see the reasoning. */
export const interpretationSchema = z.object({
  score: scoreSchema.nullable(),
  hedge: z.enum(HEDGES),
  basis: z.enum(BASES),
  affect: z.enum(AFFECTS),
  dodged: z.boolean(),
  confidence: z.number(),
  rationaleFa: z.string().nullable(),
  /** Which engine judged this — 'jev', the chat model's slug, or null if neither. */
  decidedBy: z.string().nullable(),
})
export type Interpretation = z.infer<typeof interpretationSchema>

/** The three-verdict judgement on a completion claim. */
export const claimVerdictSchema = z.object({
  verdict: z.enum(['accepted', 'needs_more', 'rejected']),
  reasoningFa: z.string().nullable(),
  /** False when no model reached a verdict — the UI shows a retry, not a result. */
  judged: z.boolean(),
})
export type ClaimVerdict = z.infer<typeof claimVerdictSchema>

export const answerResultSchema = z.object({
  interpretation: interpretationSchema.nullable(),
  /** Facts picked up incidentally from the reply. */
  factsLearned: z.array(z.object({ code: z.enum(FACT_CODES), valueFa: z.string() })),
  claimVerdict: claimVerdictSchema.nullable().optional(),
  next: moveSchema,
})
export type AnswerResult = z.infer<typeof answerResultSchema>

/* -------------------------------------------------------------------------- */
/* ledger                                                                      */
/* -------------------------------------------------------------------------- */

export const ledgerEntrySchema = z.object({
  statementCode: z.string(),
  levelNo: z.number().int(),
  textFa: z.string(),
  score: scoreSchema.nullable(),
  confidence: z.number(),
  basis: z.enum(BASES),
  rationaleFa: z.string().nullable(),
  updatedOn: z.string(),
})
export type LedgerEntry = z.infer<typeof ledgerEntrySchema>

export const dimensionSummarySchema = z.object({
  code: z.enum(DIMENSION_CODES),
  titleFa: z.string(),
  /** Mean score over statements we hold any belief about, 0–3. */
  meanScore: z.number().nullable(),
  known: z.number().int(),
  total: z.number().int(),
  /** How many of the known beliefs rest on each basis. */
  byBasis: z.record(z.enum(BASES), z.number().int()),
})
export type DimensionSummary = z.infer<typeof dimensionSummarySchema>

export const ledgerViewSchema = z.object({
  dimensions: z.array(dimensionSummarySchema),
  known: z.number().int(),
  total: z.number().int(),
})
export type LedgerView = z.infer<typeof ledgerViewSchema>

/* -------------------------------------------------------------------------- */
/* profile & domain                                                            */
/* -------------------------------------------------------------------------- */

export const orgFactSchema = z.object({
  code: z.enum(FACT_CODES),
  valueFa: z.string(),
  valueNum: z.number().nullable(),
  unit: z.string().nullable(),
  confidence: z.number(),
  /** True until the charity confirms what we inferred from conversation. */
  inferred: z.boolean(),
  source: z.enum(['conversation', 'director', 'legacy_portal']).default('conversation'),
})
export type OrgFact = z.infer<typeof orgFactSchema>

export const profileSchema = z.object({
  facts: z.array(orgFactSchema),
  sizeBand: z.enum(SIZE_BANDS).nullable(),
  sizeBandFa: z.string().nullable(),
  domains: z.array(z.object({ code: z.string(), titleFa: z.string() })),
})
export type Profile = z.infer<typeof profileSchema>

export const confirmFactsSchema = z.object({
  facts: z.array(
    z.object({
      code: z.enum(FACT_CODES),
      valueFa: z.string().max(500),
      valueNum: z.number().nullable().optional(),
    }),
  ),
})
export type ConfirmFactsRequest = z.infer<typeof confirmFactsSchema>

export const setDomainsSchema = z.object({
  codes: z.array(z.string().min(1).max(60)).min(1).max(5),
})
export type SetDomainsRequest = z.infer<typeof setDomainsSchema>

/* -------------------------------------------------------------------------- */
/* errors                                                                      */
/* -------------------------------------------------------------------------- */

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    messageFa: z.string(),
    /** Present when the caller can usefully try the same thing again. */
    retryable: z.boolean().optional(),
  }),
})
export type ApiError = z.infer<typeof apiErrorSchema>

/* -------------------------------------------------------------------------- */
/* admin — the platform tier                                                   */
/* -------------------------------------------------------------------------- */

/** One charity as the admin list shows it. Counts, never content. */
export const adminOrgSummarySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  legacyCharityId: z.number().int().nullable(),
  status: z.string(),
  createdOn: z.string(),
  users: z.number().int(),
  /** Statements with a belief, out of the canon. The one number worth a column. */
  known: z.number().int(),
  lastActivityOn: z.string().nullable(),
})
export type AdminOrgSummary = z.infer<typeof adminOrgSummarySchema>

export const adminUserSummarySchema = z.object({
  id: z.number().int(),
  email: z.string(),
  displayName: z.string().nullable(),
  role: z.enum(ROLES),
  status: z.string(),
  orgId: z.number().int(),
  orgName: z.string(),
  createdOn: z.string(),
  lastLoginOn: z.string().nullable(),
  mustChangePassword: z.boolean(),
})
export type AdminUserSummary = z.infer<typeof adminUserSummarySchema>

/**
 * Appointing staff. Only a super admin may call it, and only a super admin may
 * create another super admin — the endpoint enforces both.
 */
export const createStaffSchema = z.object({
  email: z.string().email().max(320),
  password: passwordSchema,
  displayName: z.string().min(1).max(120).optional(),
  role: z.enum(PLATFORM_ROLES),
})
export type CreateStaffRequest = z.infer<typeof createStaffSchema>

/**
 * Creating a charity account directly, password and all, rather than issuing a
 * code for someone to redeem. `orgId` names an organization that already
 * exists here; `legacyCharityId` binds a new one to the portal extract, which
 * backfills its ledger on creation; `orgName` alone starts an empty one.
 *
 * Exactly one of the three is required, which the endpoint checks rather than
 * the schema, so the Persian message can say which.
 */
export const createOrgUserSchema = z.object({
  email: z.string().email().max(320),
  password: passwordSchema,
  displayName: z.string().min(1).max(120).optional(),
  role: z.enum(TENANT_ROLES).default('org_admin'),
  orgId: z.number().int().positive().nullable().optional(),
  legacyCharityId: z.number().int().positive().nullable().optional(),
  orgName: z.string().min(1).max(300).nullable().optional(),
  mustChangePassword: z.boolean().default(true),
  /**
   * Only for a new organization bound to the portal (`legacyCharityId`): bring
   * in its old self-assessment as unconfirmed belief, and its portal profile as
   * unconfirmed facts. The admin decides; both can be done later from the
   * organization list.
   */
  importAssessment: z.boolean().default(true),
  importProfile: z.boolean().default(true),
})
export type CreateOrgUserRequest = z.infer<typeof createOrgUserSchema>

/**
 * Starting an impersonation. The reason is optional and recorded: this is a
 * support tool, and «why was someone in our account on the 3rd» is a question a
 * charity is entitled to a straight answer to.
 */
export const impersonateSchema = z.object({
  orgId: z.number().int().positive(),
  reason: z.string().max(400).nullable().optional(),
})
export type ImpersonateRequest = z.infer<typeof impersonateSchema>

export const setUserStatusSchema = z.object({
  status: z.enum(['active', 'disabled']),
})

export const resetPasswordSchema = z.object({
  password: passwordSchema,
  mustChangePassword: z.boolean().default(true),
})

/**
 * One charity as Afra's own records hold it, whether or not it has an account
 * here.
 *
 * `orgId` is the whole point of the row: null means nobody from this charity
 * has ever signed in, and `knownFromLegacy` says what its past evaluations
 * would give it the moment somebody did.
 */
export const portalCharitySchema = z.object({
  id: z.number().int(),
  title: z.string(),
  city: z.string().nullable(),
  evaluations: z.number().int(),
  /** Statements the legacy backfill already has belief for. 0 for most of them. */
  knownFromLegacy: z.number().int(),
  hasCertificate: z.boolean(),
  orgId: z.number().int().nullable(),
  orgName: z.string().nullable(),
})
export type PortalCharity = z.infer<typeof portalCharitySchema>

export const portalCharityPageSchema = z.object({
  charities: z.array(portalCharitySchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
})
export type PortalCharityPage = z.infer<typeof portalCharityPageSchema>

/** One profile value the portal holds, as it would land in the charity's profile. */
export const legacyProfileItemSchema = z.object({
  code: z.string(),
  labelFa: z.string(),
  valueFa: z.string(),
  /** What the charity's profile holds for this today, if anything. */
  currentFa: z.string().nullable(),
  /** False when a value the director gave or confirmed already stands there; it is never overwritten. */
  willWrite: z.boolean(),
})
export type LegacyProfileItem = z.infer<typeof legacyProfileItemSchema>

/** What Afra's old records hold about one organization, before anything is brought in. */
export const legacyStatusSchema = z.object({
  orgId: z.number().int(),
  legacyCharityId: z.number().int().nullable(),
  charityTitle: z.string().nullable(),
  evaluations: z.number().int(),
  lastEvaluatedOn: z.string().nullable(),
  /** Statements the old questionnaire gives belief for. */
  legacyStatements: z.number().int(),
  /** Of those, how many the ledger still holds at the old questionnaire's basis. */
  ledgerFromLegacy: z.number().int(),
  assessmentImportedOn: z.string().nullable(),
  profileImportedOn: z.string().nullable(),
  profile: z.array(legacyProfileItemSchema),
})
export type LegacyStatus = z.infer<typeof legacyStatusSchema>

export const legacyImportSchema = z.object({
  /** Binds an organization that has no portal record yet. Ignored when it already has one. */
  legacyCharityId: z.number().int().positive().nullable().optional(),
  assessment: z.boolean(),
  profile: z.boolean(),
})
export type LegacyImportRequest = z.infer<typeof legacyImportSchema>

export const legacyImportResultSchema = z.object({
  inserted: z.number().int(),
  refreshed: z.number().int(),
  factsWritten: z.number().int(),
  factsKept: z.number().int(),
})
export type LegacyImportResult = z.infer<typeof legacyImportResultSchema>

/* -------------------------------------------------------------------------- */
/* the journey as a conversation                                               */
/* -------------------------------------------------------------------------- */

/**
 * One line of the transcript.
 *
 * A turn row holds both halves of an exchange — what was asked and what came
 * back — so the server flattens it into messages rather than making the client
 * work out which fields belong to whom. `readingFa` is what we *took* from an
 * answer, shown under it: a conversation where the agent silently scores you is
 * the thing this product exists not to be.
 */
/** One ready-made answer. `score` is a hint about what was asked, never a grade. */
export const askOptionSchema = z.object({
  labelFa: z.string(),
  score: scoreSchema.nullable(),
})
export type AskOption = z.infer<typeof askOptionSchema>

export const chatMessageSchema = z.object({
  id: z.string(),
  turnId: z.number().int(),
  /**
   * A tool row is part of the conversation, not a footnote to it — the agent
   * writing to a charity's ledger mid-sentence should be visible. Whether these
   * reach the client at all is the `showToolCalls` policy.
   */
  kind: z.enum(['message', 'tool']),
  from: z.enum(['agent', 'org']),
  textFa: z.string(),
  at: z.string(),
  moveType: z.string().nullable(),
  /** Provenance, for the badge. False means stored phrasing, not a model. */
  generated: z.boolean(),
  model: z.string().nullable(),
  readingFa: z.string().nullable(),
  score: scoreSchema.nullable(),
  basis: z.enum(BASES).nullable(),
  skipped: z.boolean(),
  notApplicable: z.boolean(),
  /** Set on a tool row. */
  toolName: z.string().nullable(),
  toolSummaryFa: z.string().nullable(),
  toolFailed: z.boolean(),
  /** Set on an agent question that came with ready-made answers. */
  options: z.array(askOptionSchema).nullable(),
  allowCustom: z.boolean(),
  /** Set on a question that puts one of the agent's guesses to the director. */
  hypothesisId: z.number().int().nullable().default(null),
})
export type ChatMessage = z.infer<typeof chatMessageSchema>

/** What this journey is for. Both halves are the organization's own words. */
export const journeyGoalSchema = z.object({
  challengeFa: z.string().nullable(),
  outcomeFa: z.string().nullable(),
  challengeSetOn: z.string().nullable(),
  outcomeSetOn: z.string().nullable(),
})
export type JourneyGoal = z.infer<typeof journeyGoalSchema>

export const setGoalSchema = z.object({
  challengeFa: z.string().max(1000).nullable().optional(),
  outcomeFa: z.string().max(1000).nullable().optional(),
})

/** A statement we are still carrying, raised by an answer that opened it. */
export const notedItemSchema = z.object({
  id: z.number().int(),
  kindFa: z.string(),
  textFa: z.string(),
  at: z.string(),
  statementCode: z.string().nullable(),
})
export type NotedItem = z.infer<typeof notedItemSchema>

/** A guess at the root cause, and how far the conversation has tested it. */
export const hypothesisSchema = z.object({
  id: z.number().int(),
  textFa: z.string(),
  status: z.enum(['open', 'supported', 'weakened', 'confirmed', 'refuted']),
  evidenceFa: z.string().nullable(),
  at: z.string(),
  /** When the guess was put to the director. Null means they have not been asked yet. */
  askedOn: z.string().nullable().default(null),
})
export type Hypothesis = z.infer<typeof hypothesisSchema>

/** What we believe we know about the organization itself, and how firmly. */
export const knownFactSchema = z.object({
  code: z.string(),
  labelFa: z.string(),
  valueFa: z.string(),
  inferred: z.boolean(),
  at: z.string(),
  /** Heard in conversation, given by the director, or brought in from the portal. */
  source: z.enum(['conversation', 'director', 'legacy_portal']).default('conversation'),
})
export type KnownFact = z.infer<typeof knownFactSchema>

export const dimensionCoverageSchema = z.object({
  code: z.enum(DIMENSION_CODES),
  titleFa: z.string(),
  known: z.number().int(),
  total: z.number().int(),
  /** The dimension the challenge points at, walked first. */
  focus: z.boolean(),
})
export type DimensionCoverage = z.infer<typeof dimensionCoverageSchema>

/**
 * A task the agent proposed and the organization took on.
 *
 * `doneLooksLikeFa` is written when the task is made, not when it is claimed —
 * otherwise «done» is whatever the claimant says it is. The verdict is a
 * model's, and `lastModel` is null exactly when no model reached one, which the
 * UI must render as "not checked yet" and never as "failed".
 */
export const taskSchema = z.object({
  id: z.number().int(),
  actionFa: z.string(),
  doneLooksLikeFa: z.string(),
  dueOn: z.string().nullable(),
  /** The deadline in the Persian calendar, and where an open task stands against it — computed on the server. */
  dueOnFa: z.string().nullable().default(null),
  timing: z.enum(['no_due_date', 'not_due', 'due_soon', 'due_today', 'overdue']).nullable().default(null),
  daysLeft: z.number().int().nullable().default(null),
  status: z.enum(['open', 'done', 'abandoned']),
  statementCode: z.string().nullable(),
  levelNo: z.number().int().nullable(),
  createdOn: z.string(),
  lastVerdict: z.enum(['accepted', 'needs_more', 'rejected']).nullable(),
  lastReasoningFa: z.string().nullable(),
  lastModel: z.string().nullable(),
  attempts: z.number().int(),
  /** An open task is either waiting or under way; only a judged claim makes it done. */
  progress: z.enum(['todo', 'doing']).default('todo'),
  ownerFa: z.string().nullable().default(null),
  /** Claims made so far. The acceptance test stops being editable after the first. */
  claims: z.number().int().default(0),
})
export type Task = z.infer<typeof taskSchema>

/** Where the conversation is up to: this chapter, and how many are behind it. */
export const chapterStateSchema = z.object({
  seq: z.number().int(),
  goalFa: z.string(),
  closedCount: z.number().int(),
})
export type ChapterState = z.infer<typeof chapterStateSchema>

/** Everything the side panel shows. One request, because it is one panel. */
export const journeyContextSchema = z.object({
  goal: journeyGoalSchema,
  chapter: chapterStateSchema,
  dimensions: z.array(dimensionCoverageSchema),
  noted: z.array(notedItemSchema),
  hypotheses: z.array(hypothesisSchema),
  facts: z.array(knownFactSchema),
  tasks: z.array(taskSchema),
  progress: progressSchema,
})
export type JourneyContext = z.infer<typeof journeyContextSchema>

/**
 * A message the organization sends.
 *
 * `pickedLabelFa` is the option they clicked, when the question came with any.
 * It arrives separately from their own words so the two are distinguishable in
 * the record — «انتخاب کرد» and «نوشت» are different kinds of evidence.
 */
export const saySchema = z.object({
  textFa: z.string().max(4000).optional().default(''),
  pickedLabelFa: z.string().max(400).nullable().optional(),
})

/**
 * Archiving the live conversation and starting a fresh one. Nothing is
 * deleted; the only choice is whether open tasks come along.
 */
export const archiveChatSchema = z.object({
  carryOpenTasks: z.boolean().default(true),
})
export type ArchiveChatRequest = z.infer<typeof archiveChatSchema>

/** One archived conversation in the history list. */
export const archivedChatSummarySchema = z.object({
  id: z.number().int(),
  startedOn: z.string(),
  archivedOn: z.string(),
  archivedByEmail: z.string().nullable(),
  challengeFa: z.string().nullable(),
  outcomeFa: z.string().nullable(),
  firstSaidFa: z.string().nullable(),
  saidCount: z.number().int(),
  hypothesisCount: z.number().int(),
  confirmedCount: z.number().int(),
  taskCount: z.number().int(),
  noteCount: z.number().int(),
})
export type ArchivedChatSummary = z.infer<typeof archivedChatSummarySchema>

/** An archived conversation, read-only: what was said and what it produced. */
export const archivedChatSchema = z.object({
  id: z.number().int(),
  startedOn: z.string(),
  archivedOn: z.string(),
  goal: journeyGoalSchema,
  messages: z.array(chatMessageSchema),
  showTools: z.boolean(),
  hypotheses: z.array(hypothesisSchema),
  noted: z.array(notedItemSchema),
  tasks: z.array(taskSchema),
})
export type ArchivedChat = z.infer<typeof archivedChatSchema>

export const claimTaskSchema = z.object({
  accountFa: z.string().min(10).max(4000),
  documentId: z.number().int().positive().nullable().optional(),
})

/**
 * Changing a task from the planning page. Every field is optional; `dueInDays`
 * null clears the deadline. There is no `status: done` here — a task closes
 * through a judged claim and nowhere else.
 */
export const updateTaskSchema = z.object({
  actionFa: z.string().min(3).max(1000).optional(),
  doneLooksLikeFa: z.string().min(3).max(1000).optional(),
  ownerFa: z.string().max(200).nullable().optional(),
  dueInDays: z.number().int().min(0).max(730).nullable().optional(),
  progress: z.enum(['todo', 'doing']).optional(),
  /** Set the task aside, or bring a set-aside task back. */
  status: z.enum(['open', 'abandoned']).optional(),
})
export type UpdateTaskRequest = z.infer<typeof updateTaskSchema>

/* -------------------------------------------------------------------------- */
/* the development picture (025)                                               */
/* -------------------------------------------------------------------------- */

/** One proposal on «برنامه توسعه پیشنهادی». */
export const roadmapItemSchema = z.object({
  id: z.number().int(),
  priority: z.number().int(),
  kind: z.enum(['priority', 'intervention']),
  dimensionCode: z.enum(DIMENSION_CODES),
  dimensionTitleFa: z.string(),
  actionFa: z.string(),
  rationaleFa: z.string().nullable(),
  outcomeFa: z.string().nullable(),
  doneLooksLikeFa: z.string(),
  hypothesisFa: z.string().nullable(),
  status: z.enum(['proposed', 'planned', 'dismissed']),
  commitmentId: z.number().int().nullable(),
})
export type RoadmapItem = z.infer<typeof roadmapItemSchema>

/** One row of «تصویر توسعه در همه ابعاد». `relation` null means not looked at yet. */
export const dimensionPictureSchema = z.object({
  code: z.enum(DIMENSION_CODES),
  titleFa: z.string(),
  relation: z.enum(['deep_dive', 'related', 'reviewed']).nullable(),
  findingFa: z.string().nullable(),
  known: z.number().int(),
  total: z.number().int(),
})
export type DimensionPicture = z.infer<typeof dimensionPictureSchema>

export const roadmapViewSchema = z.object({
  goal: journeyGoalSchema,
  rootCauses: z.array(hypothesisSchema),
  items: z.array(roadmapItemSchema),
  dimensions: z.array(dimensionPictureSchema),
})
export type RoadmapView = z.infer<typeof roadmapViewSchema>

export const planRoadmapItemSchema = z.object({
  doneLooksLikeFa: z.string().min(3).max(1000).optional(),
  ownerFa: z.string().max(200).nullable().optional(),
  dueInDays: z.number().int().min(0).max(730).nullable().optional(),
})
export type PlanRoadmapItemRequest = z.infer<typeof planRoadmapItemSchema>

/** «داشبورد» and «پایش پیشرفت». Computed on every read; see modules/dashboard. */
export const dashboardViewSchema = z.object({
  overall: z.object({ percent: z.number(), known: z.number().int(), total: z.number().int() }),
  badge: z.object({
    awardedTier: z.string().nullable(),
    conditionalTier: z.string().nullable(),
    authoritative: z.boolean(),
  }),
  levels: z.array(
    z.object({
      levelNo: z.number().int(),
      tier: z.string(),
      titleFa: z.string(),
      percent: z.number(),
      known: z.number().int(),
      total: z.number().int(),
    }),
  ),
  dimensions: z.array(
    z.object({
      code: z.enum(DIMENSION_CODES),
      titleFa: z.string(),
      percent: z.number(),
      known: z.number().int(),
      total: z.number().int(),
      claimsOnly: z.number().int(),
    }),
  ),
  gaps: z.array(
    z.object({
      code: z.string(),
      levelNo: z.number().int(),
      dimensionTitleFa: z.string(),
      textFa: z.string(),
      score: z.number().nullable(),
      basis: z.string().nullable(),
      isKey: z.boolean(),
    }),
  ),
  tasks: z.object({ open: z.number().int(), overdue: z.number().int(), done: z.number().int(), total: z.number().int() }),
  reminders: z.array(z.object({ textFa: z.string(), tone: z.enum(['bad', 'warn', 'info']) })),
  trend: z.array(z.object({ on: z.string(), percent: z.number(), known: z.number().int() })),
  review: z.object({ status: z.string(), submittedOn: z.string() }).nullable(),
})
export type DashboardView = z.infer<typeof dashboardViewSchema>

/** The director's own verdict on one of the agent's guesses. */
export const hypothesisVerdictSchema = z.object({
  verdict: z.enum(['confirmed', 'refuted']),
})

/** Platform-wide policy a super admin can change. */
export const platformSettingsSchema = z.object({
  showToolCalls: z.boolean(),
})
export type PlatformSettings = z.infer<typeof platformSettingsSchema>
